// app/context/review-context.tsx
// Client-side review management with fetching, caching, and loading states
// Prevents duplicate API calls and ensures consistent data across components
//
// Related files:
//   - ~/routes/api.reviews.tsx - API endpoint for review operations
//   - ~/components/universal-product-card.tsx - Uses reviews for star ratings
//   - ~/components/product-modal/modal-product-card.tsx - Uses reviews in modal
//   - ~/routes/_layout.product.$slug/route.tsx - Uses reviews on product page
//   - ~/components/product-reviews.tsx - Full review display component

import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  ReactNode,
  startTransition,
} from 'react';

export interface Review {
  id: string;
  user_id: string;
  product_id: string;
  rating: number;
  comment: string;
  created: string;
  status: string;
  user: {
    created_at: string;
    email: string;
    raw_app_meta_data: string;
  } | null;
}

interface ReviewContextType {
  getReviews: (productId: string) => Review[] | null;
  isLoadingReviews: (productId: string) => boolean;
  fetchReviews: (productId: string) => Promise<void>;
  fetchReviewsBatch: (productIds: string[]) => Promise<void>;
  setReviews: (productId: string, reviews: Review[]) => void;
  clearCache: () => void;
  getUserReview: (productId: string, userId: string) => Promise<Review | null>;
}

const ReviewContext = createContext<ReviewContextType | undefined>(undefined);

export function ReviewProvider({ children }: { children: ReactNode }) {
  const [reviewCache, setReviewCache] = useState<Record<string, Review[]>>({});
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});

  // Track pending requests to prevent duplicate fetches
  const pendingRequests = useRef<Set<string>>(new Set());
  const abortControllers = useRef<Record<string, AbortController>>({});

  const getReviews = useCallback(
    (productId: string): Review[] | null => {
      return reviewCache[productId] || null;
    },
    [reviewCache],
  );

  const isLoadingReviews = useCallback(
    (productId: string): boolean => {
      return loadingStates[productId] || false;
    },
    [loadingStates],
  );

  const setReviews = useCallback((productId: string, reviews: Review[]) => {
    startTransition(() => {
      setReviewCache((prev) => ({
        ...prev,
        [productId]: reviews,
      }));
      setLoadingStates((prev) => ({
        ...prev,
        [productId]: false,
      }));
    });
    pendingRequests.current.delete(productId);
  }, []);

  const fetchReviews = useCallback(
    async (productId: string): Promise<void> => {
      // Return cached reviews immediately if available
      if (reviewCache[productId]) {
        return;
      }

      // Skip if already fetching
      if (pendingRequests.current.has(productId)) {
        return;
      }

      // Cancel previous request for this product if exists
      if (abortControllers.current[productId]) {
        abortControllers.current[productId].abort();
      }

      // Mark as loading and pending
      startTransition(() => {
        setLoadingStates((prev) => ({ ...prev, [productId]: true }));
      });
      pendingRequests.current.add(productId);

      // Create new abort controller
      const abortController = new AbortController();
      abortControllers.current[productId] = abortController;

      try {
        const response = await fetch('/api/reviews', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            actionType: 'GET',
            product_id: productId,
          }),
          signal: abortController.signal,
        });

        if (abortController.signal.aborted) {
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch reviews: ${response.status}`);
        }

        const data = await response.json();

        if (abortController.signal.aborted) {
          return;
        }

        if (data?.reviews) {
          setReviews(productId, data.reviews);
        } else {
          // No reviews found - cache empty array to prevent refetching
          setReviews(productId, []);
        }
      } catch (error: any) {
        if (error.name === 'AbortError') {
          return;
        }
        console.error(`Error fetching reviews for product ${productId}:`, error);
        // Set empty array on error to prevent infinite retries
        setReviews(productId, []);
      } finally {
        // Cleanup
        delete abortControllers.current[productId];
      }
    },
    [reviewCache, setReviews],
  );

  const fetchReviewsBatch = useCallback(
    async (productIds: string[]): Promise<void> => {
      if (productIds.length === 0) return;

      // Filter out products that are already cached or being fetched
      const idsToFetch = productIds.filter(
        (id) => !reviewCache[id] && !pendingRequests.current.has(id),
      );

      if (idsToFetch.length === 0) return;

      // Mark all as loading
      startTransition(() => {
        setLoadingStates((prev) => {
          const next = { ...prev };
          idsToFetch.forEach((id) => {
            next[id] = true;
          });
          return next;
        });
      });

      // Mark all as pending
      idsToFetch.forEach((id) => pendingRequests.current.add(id));

      // Create abort controller for batch request
      const abortController = new AbortController();
      const requestKey = idsToFetch.sort().join(',');
      abortControllers.current[requestKey] = abortController;

      try {
        const response = await fetch('/api/reviews', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            actionType: 'BATCH_GET',
            product_ids: idsToFetch,
          }),
          signal: abortController.signal,
        });

        if (abortController.signal.aborted) {
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch batch reviews: ${response.status}`);
        }

        const data = await response.json();

        if (abortController.signal.aborted) {
          return;
        }

        if (data?.reviewsByProductId) {
          // Update cache for all products
          Object.entries(data.reviewsByProductId).forEach(([productId, reviews]) => {
            setReviews(productId, reviews as Review[]);
          });

          // Mark products not in response as having no reviews
          idsToFetch.forEach((id) => {
            if (!data.reviewsByProductId[id]) {
              setReviews(id, []);
            }
          });
        } else {
          // No reviews found for any - cache empty arrays
          idsToFetch.forEach((id) => setReviews(id, []));
        }
      } catch (error: any) {
        if (error.name === 'AbortError') {
          return;
        }
        console.error('Error fetching batch reviews:', error);
        // Set empty arrays on error
        idsToFetch.forEach((id) => setReviews(id, []));
      } finally {
        // Cleanup
        delete abortControllers.current[requestKey];
      }
    },
    [reviewCache, setReviews],
  );

  const getUserReview = useCallback(
    async (productId: string, userId: string): Promise<Review | null> => {
      if (!productId || !userId) {
        return null;
      }

      try {
        const response = await fetch('/api/reviews', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            actionType: 'GET_USER_REVIEW',
            product_id: productId,
            user_id: userId,
          }),
        });

        if (!response.ok) {
          console.error('Failed to fetch user review:', response.status);
          return null;
        }

        const data = await response.json();
        return data?.review || null;
      } catch (error) {
        console.error(`Error fetching user review for product ${productId}:`, error);
        return null;
      }
    },
    [],
  );

  const clearCache = useCallback(() => {
    setReviewCache({});
    setLoadingStates({});
    pendingRequests.current.clear();
    // Cancel all pending requests
    Object.values(abortControllers.current).forEach((controller) => controller.abort());
    abortControllers.current = {};
  }, []);

  return (
    <ReviewContext.Provider
      value={{
        getReviews,
        isLoadingReviews,
        fetchReviews,
        fetchReviewsBatch,
        setReviews,
        clearCache,
        getUserReview,
      }}
    >
      {children}
    </ReviewContext.Provider>
  );
}

export function useReviewCache() {
  const context = useContext(ReviewContext);
  if (context === undefined) {
    throw new Error('useReviewCache must be used within a ReviewProvider');
  }
  return context;
}
